import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import tr from './locales/tr';
import en from './locales/en';

/**
 * Portal languages. Migrated screens read their texts through
 * useTranslation(); legacy pages are still Turkish-only and simply ignore it.
 */
export const LANGUAGES = [
  { code: 'tr', label: 'Türkçe', short: 'TR', locale: 'tr-TR' },
  { code: 'en', label: 'English', short: 'EN', locale: 'en-GB' },
];

const DICTIONARIES = { tr, en };
const DEFAULT_LANGUAGE = 'tr';
const STORAGE_KEY = 'language';

const readStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return DICTIONARIES[stored] ? stored : DEFAULT_LANGUAGE;
  } catch (error) {
    return DEFAULT_LANGUAGE;
  }
};

const lookup = (dictionary, key) =>
  key.split('.').reduce((node, part) => (node == null ? undefined : node[part]), dictionary);

const interpolate = (template, vars) =>
  template.replace(/\{\{(\w+)\}\}/g, (match, name) => (vars && vars[name] !== undefined ? String(vars[name]) : match));

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readStoredLanguage);

  const setLanguage = useCallback((code) => {
    if (!DICTIONARIES[code]) return;
    setLanguageState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch (error) {
      // Storage may be unavailable (private mode); the switch still applies.
    }
  }, []);

  /**
   * t('dashboard.tasks.title') / t('topbar.newTasks', { count: 3 }).
   * With a numeric `count`, `<key>_one` / `<key>_other` win when present.
   * Missing in the current language → Turkish text → the key itself.
   */
  const t = useCallback((key, vars) => {
    const dictionary = DICTIONARIES[language];
    let value;
    if (vars && typeof vars.count === 'number') {
      value = lookup(dictionary, `${key}_${vars.count === 1 ? 'one' : 'other'}`);
    }
    if (value === undefined) value = lookup(dictionary, key);
    if (value === undefined) value = lookup(DICTIONARIES[DEFAULT_LANGUAGE], key);
    if (typeof value !== 'string') {
      if (process.env.NODE_ENV !== 'production') console.warn(`[i18n] missing text: ${key}`);
      return key;
    }
    return interpolate(value, vars);
  }, [language]);

  // Menu titles are keyed by route path; Turkish uses menuConfig's own titles.
  const tMenu = useCallback(
    (path, fallback) => DICTIONARIES[language].menu?.[path] || fallback,
    [language]
  );

  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
    tMenu,
    locale: LANGUAGES.find((entry) => entry.code === language).locale,
  }), [language, setLanguage, t, tMenu]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useTranslation = () => useContext(LanguageContext);
